/*
jQuery plugin


```
import debug from 'debug';
import plugin from '@core/plugins/lib';

const ELEMENT_SELECTOR = '[data-my-plugin]';
const PLUGIN_NAME = 'MyPlugin';
const MODULE_NAME = 'plugins/my-plugin';
const log = debug(MODULE_NAME);

class PluginKlass {
    constructor(element, options) {
      log('new instance', element, options);

      this.element = element;
      this.options = options;
      this.$element = $(element);

      log('ready', this.$element);

      this.init();
    }
}

PluginKlass.DEFAULTS = {
  someCamelCasedProperty: `${PLUGIN_NAME}:something`,
};

plugin(PLUGIN_NAME, PluginKlass);

$(document).ready(() => {
  $(ELEMENT_SELECTOR)[PLUGIN_NAME]();
});
```

Then using it in html:
```
<div class="something-else"
     data-my-plugin
     data-my-plugin--some-camel-cased-property="a-string:something-else"></div>
```

Styelguide Core.Global.Javascript.JqueryPlugin
*/
import $ from 'jquery';
import debug from 'debug';

import * as fn from './fn';
import * as strings from './strings';
import * as objects from './objects';
import * as selectors from './selectors';
// import * as events from './events';

import {toCamelCase}  from './strings';
import { selectTarget } from './selectors';

const log = debug('core/plugins/lib');

export default function plugin(pluginName, klass, shortHand = false) {
  const formattedPluginName = toCamelCase(pluginName);
  const dataName = `__${formattedPluginName}`;
  const old = $.fn[formattedPluginName];
  const ZERO = 0;

  klass.$bus = {
      subscribe: function(event, fn) {
          $(this).bind(event, fn);
      },
      publish: function(event, data) {
          $(this).trigger(event, [data]);
      }
  };

  klass.$util = {
    fn,
    objects,
    strings,
    selectors,
  };

  klass.prototype.selectors = selectors;
  klass.prototype.strings = strings;
  klass.prototype.objects = objects;
  klass.prototype.fn = fn;
  klass.prototype.inViewport = function (elm) {
    var rect = elm.getBoundingClientRect();
    var viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
  };

  $.fn[pluginName] = function PluginFactory (option) {
    return this.each(function PluginInstanceFactory () {
      const $this = $(this);
      const data = $this.data();
      const overrides = typeof option === 'object' && option;
      const elementOptions = Object
          .keys(data)
          .filter((key) => {
            return toCamelCase(key).indexOf(formattedPluginName) >= ZERO;
          })
          .reduce((result, path) => {
            const data = path && $this.data(path);
            const key = toCamelCase(path.replace(new RegExp(`${formattedPluginName}-?`), ''));
            log(path, key, $this.data(path));

            if (key && data !== undefined) {
              result[key] = data;
            }
            return result;
          }, {});
      log(pluginName, { elementOptions});
      let instance = $this.data(dataName);
      const options = $.extend({}, klass.DEFAULTS, elementOptions, overrides);

      // store reference to instance
      if (!instance) {
        instance = new klass(this, options);
        instance.$utils = klass.$util;
        $this.data(dataName, instance);
        $this.addClass(`u-${pluginName}`);
      }

      // execute instance method
      if (typeof option === 'string') {
        instance[option]();
      }
    });
  };

  // - Short hand
  if (shortHand) {
    $[formattedPluginName] = (options) => $({})[formattedPluginName](options);
  }

  // - No conflict
  // $.fn[formattedPluginName].noConflict = function noConflict () {
  //   $.fn[formattedPluginName] = old;
  // };
}

export function jquery (name) {
    return function decorate (target) {
        plugin(name, target);
    };
}

export function defaults (options) {
    return function decorate (target) {
        target.DEFAULTS = options;
    };
}



// WEBPACK FOOTER //
// ./client/src/core/plugins/lib/index.js